import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { getErrorHttpMessage } from "utils";
import { getInventarioHttp } from "actions/inventario";
import { getUnidadesMedidaHttp } from "actions/unidadMedida";
import { getCategoriasHttp } from "actions/categorias";
import { getUbicacionesHttp } from "actions/ubicacion";
import { getTipoProductosHttp } from "actions/tipoProducto";
import { getProveedoresHttp } from "actions/proveedores";
import type {
    CatalogosInventario,
    FilaInventario,
    QrProducto,
    ValoresFormularioInventario,
} from "../types/catalogoInventario.types";
import {
    normalizarCatalogo,
    normalizarInventario,
} from "../utils/catalogoInventarioNormalizar.utils";
import {
    activarProductoInventario,
    crearProductoInventario,
    desactivarProductoInventario,
    editarProductoInventario,
} from "../utils/catalogoInventarioOperaciones.utils";

const CATALOGOS_VACIOS: CatalogosInventario = {
    unidadesMedida: [],
    categorias: [],
    ubicaciones: [],
    tipoProductos: [],
    proveedores: [],
};

export const useCatalogoInventario = (idUsuario: number) => {
    const intl = useIntl();

    const [filas, setFilas] = useState<FilaInventario[]>([]);
    const [catalogos, setCatalogos] = useState<CatalogosInventario>(CATALOGOS_VACIOS);
    const [cargando, setCargando] = useState(false);
    const [guardando, setGuardando] = useState(false);
    const [mensaje, setMensaje] = useState<string | null>(null);

    const mostrarError = useCallback(
        (error: unknown, idMensaje: string) => {
            setMensaje(getErrorHttpMessage(error) || intl.formatMessage({ id: idMensaje }));
        },
        [intl]
    );

    const mostrarMensaje = useCallback((texto: string) => setMensaje(texto), []);

    const cargarInventario = useCallback(async () => {
        try {
            setCargando(true);
            setFilas(normalizarInventario(await getInventarioHttp()));
        } catch (error) {
            mostrarError(error, "get_elementos_error");
        } finally {
            setCargando(false);
        }
    }, [mostrarError]);

    const cargarCatalogos = useCallback(async () => {
        try {
            const [unidades, categorias, ubicaciones, tipos, proveedores] = await Promise.all([
                getUnidadesMedidaHttp(),
                getCategoriasHttp(),
                getUbicacionesHttp(),
                getTipoProductosHttp(),
                getProveedoresHttp(),
            ]);
            setCatalogos({
                unidadesMedida: normalizarCatalogo(unidades),
                categorias: normalizarCatalogo(categorias),
                ubicaciones: normalizarCatalogo(ubicaciones, "id_ubicacion"),
                tipoProductos: normalizarCatalogo(tipos),
                proveedores: normalizarCatalogo(proveedores, "id", "nombre_comercial"),
            });
        } catch (error) {
            mostrarError(error, "get_elementos_error");
        }
    }, [mostrarError]);

    useEffect(() => {
        cargarInventario();
        cargarCatalogos();
    }, [cargarInventario, cargarCatalogos]);

    /** Regresa true si se guardó, para que el formulario sepa si cerrarse. */
    const crear = async (
        valores: ValoresFormularioInventario,
        proveedores: number[]
    ): Promise<boolean> => {
        try {
            setGuardando(true);
            await crearProductoInventario(valores, idUsuario, proveedores);
            await cargarInventario();
            setMensaje(intl.formatMessage({ id: "general_añadido_correctamente" }));
            return true;
        } catch (error) {
            mostrarError(error, "error_añadir_general");
            return false;
        } finally {
            setGuardando(false);
        }
    };

    const editar = async (
        idInventario: number,
        valores: ValoresFormularioInventario
    ): Promise<boolean> => {
        try {
            setGuardando(true);
            await editarProductoInventario(idInventario, valores, idUsuario);
            // Se recarga para traer los nombres de categoría, ubicación, etc. ya resueltos.
            await cargarInventario();
            setMensaje(intl.formatMessage({ id: "general_editado_correctamente" }));
            return true;
        } catch (error) {
            mostrarError(error, "general_editado_error");
            return false;
        } finally {
            setGuardando(false);
        }
    };

    const cambiarEstatus = async (fila: FilaInventario, activar: boolean) => {
        try {
            setCargando(true);
            await (activar
                ? activarProductoInventario(fila.id)
                : desactivarProductoInventario(fila.id));
            setFilas((prev) => prev.map((f) => (f.id === fila.id ? { ...f, activo: activar } : f)));
            setMensaje(
                intl.formatMessage({
                    id: activar ? "general_activado_correctamente" : "general_desactivado_correctamente",
                })
            );
        } catch (error) {
            mostrarError(error, activar ? "general_activado_error" : "general_desactivado_error");
        } finally {
            setCargando(false);
        }
    };

    /** Pone en las filas los QR recién generados, sin recargar todo el inventario. */
    const asignarQrs = useCallback((qrs: QrProducto[]) => {
        if (qrs.length === 0) return;
        const porId = new Map(qrs.map((q) => [q.idInventario, q]));
        setFilas((prev) => prev.map((f) => (porId.has(f.id) ? { ...f, qr: porId.get(f.id) ?? null } : f)));
    }, []);

    return {
        filas,
        catalogos,
        cargando,
        guardando,
        mensaje,
        cerrarMensaje: () => setMensaje(null),
        mostrarError,
        mostrarMensaje,
        crear,
        editar,
        activar: (fila: FilaInventario) => cambiarEstatus(fila, true),
        desactivar: (fila: FilaInventario) => cambiarEstatus(fila, false),
        asignarQrs,
    };
};
